import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DownloadStatus } from 'src/app/shared/types/download-button-types';
import { Division } from './ebr-api-client/ebr-api-client.g';

@Injectable({
  providedIn: 'root',
})
/**
 * Service to manage the state of spreadsheet download across the application.
 * It uses a BehaviorSubject to keep track of the current status of the spreadsheet download,
 * allowing components to subscribe and react to changes in that status.
 * The service also includes a method to resolve the button text based on the current download status and division,
 * ensuring that the UI updates reactively based on these factors.
 */
export class SpreadsheetService {
  private spreadsheetDownloadStatus =
    new BehaviorSubject<DownloadStatus>(
      DownloadStatus.Idle,
    );
  downloadStatus$ = this.spreadsheetDownloadStatus.asObservable();

  // Method to update the download status
  updateSpreadsheetDownloadStatus(status: DownloadStatus) {
    this.spreadsheetDownloadStatus.next(status);
  }

  /**
   * Resolve the button text based on the download status and division.
   */
  resolveSpreadsheetDownloadButtonText(
    status: DownloadStatus,
    division: Division | undefined,
  ): string {
    if (status === DownloadStatus.Running) {
      return 'Processing Download...';
    }

    if (status === DownloadStatus.Idle) {
      return division === Division.Pest
        ? 'Download Spreadsheet'
        : 'Download Service Call Data Spreadsheet';
    }

    // Fallback to older button text when status is not Downloading or Idle
    return 'Download Spreadsheet';
  }

  /**
   * Resolve the longest possible button text based on the division.
   * This is used to maintain consistent button width in the UI.
   * TODO: This is a quick HACK, need to find better ways to drive consistency and responsiveness since
   * hardcoding button width is not ideal.
   */
  resolveLongestButtonText(division: Division | undefined): string {
    return division === Division.Pest
      ? 'Processing Download...' // Longer of the two possible labels for Pest
      : 'Download Service Call Data Spreadsheet';
  }
}
